import { Button, Descriptions, Space, Typography } from '@arco-design/web-react';
import { IconRobot } from '@arco-design/web-react/icon';
import AiBadge from '../AiBadge';
import CopyText from '../CopyText';
import DetailSection from '../DetailSection';
import EmptyValue from '../EmptyValue';
import PillTag from '../PillTag';
import ScoreTag from '../ScoreTag';
import StatusTag from '../StatusTag';
import { authUrl, field, fmtDuration, fmtTime } from '../../utils/format';
import type { Cdr } from '../../api/cdr';
import type { CallSummary } from '../../api/callSummaries';
import { CallBackLink, HangupCauseTag } from './CallDetailBits';
import { TranscriptView } from './CallTranscriptView';

export interface CallDetailPaneProps {
  /** 通话详情（基本信息/录音/转写/小结的数据源） */
  detail: Cdr;
  /** AI 会话横幅展示的意图名（取实时识别日志；无则不出胶囊） */
  intentName?: string;
  /** AI 调用日志条数（横幅文案用） */
  aiLogCount: number;
  /** 通话录音播放器引用：入口持有（关闭抽屉/切换 CDR 时暂停） */
  audioRef: React.RefObject<HTMLAudioElement>;
  /** 服务小结（null = 未填写，展示补录入口） */
  summary: CallSummary | null;
  /** 通话时长（秒）：与录音说明同轴，入口已按 talkSeconds 计算 */
  talkSec: number;
  /** 等待接通时长（秒）：为 0 时不占位 */
  waitSec: number;
  /** 补录服务小结 */
  onSupplementSummary: () => void;
  /** 查看关联工单详情（本页叠层，不跳工单管理页） */
  onOpenTicket: (ticketId: number) => void;
}

/**
 * 通话详情「通话详情」页签内容：AI 会话横幅 + 基本信息 + 录音 + 转写 + 服务小结。
 * 只读展示为主，补录小结/跳关联工单由入口侧传入（本分片不持有状态）。
 */
export default function CallDetailPane({
  detail,
  intentName,
  aiLogCount,
  audioRef,
  summary,
  talkSec,
  waitSec,
  onSupplementSummary,
  onOpenTicket,
}: CallDetailPaneProps) {
  const recordUrl = field<string>(detail, 'record_url', 'recordUrl');
  const satisfaction = detail.satisfaction_score;
  return (
    <Space direction="vertical" size="large" style={{ width: '100%' }}>
      {detail.ai_session && (
        <div className="bcc-ai-banner" style={{ padding: '10px 14px' }}>
          <div className="bcc-ai-banner-icon" style={{ width: 34, height: 34, borderRadius: 9 }}>
            <IconRobot style={{ fontSize: 18 }} />
          </div>
          <div className="bcc-ai-banner-txt">
            <b style={{ fontSize: 14 }}>AI 智能会话{detail.agent_id ? ' · 已转人工' : ''}</b>
            <p style={{ margin: '3px 0 0', display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
              {intentName && <AiBadge>{intentName}</AiBadge>}
              <span>AI 调用日志 {aiLogCount} 条 · 阶段明细见「AI 调用日志」页签</span>
            </p>
          </div>
        </div>
      )}
      <Descriptions
        column={2}
        title="基本信息"
        data={[
          { label: '方向', value: <StatusTag value={detail.direction} /> },
          { label: '状态', value: <HangupCauseTag cause={detail.hangup_cause} connected={!!detail.answer_time} /> },
          { label: '主叫', value: <CallBackLink number={detail.caller} /> },
          {
            label: '被叫',
            // 呼入转接后 callee 为转接目的地，展示客户实际拨打的原始被叫；
            // 外呼的 original_callee 即客户号码（=主叫），直接展示 callee
            value: (
              <CallBackLink
                number={detail.direction === 'inbound' ? detail.original_callee || detail.callee : detail.callee}
              />
            ),
          },
          { label: '坐席', value: detail.agent_name || detail.agent_extension || <EmptyValue /> },
          {
            label: '会话类型',
            value: detail.ai_session ? <AiBadge>AI</AiBadge> : <PillTag color="arcoblue">人工</PillTag>,
          },
          { label: '开始时间', value: fmtTime(detail.start_time || detail.created_at) },
          // 接通时间单列：通话时长与录音都以它为轴，缺少这行时「详情时长」与「录音时长」对不上无法自查
          { label: '接通时间', value: detail.answer_time ? fmtTime(detail.answer_time) : <EmptyValue /> },
          { label: '结束时间', value: fmtTime(detail.end_time) },
          // 等待接通（呼出=振铃，呼入=IVR/排队+振铃）只在确实等待过时占位，0 秒不显示噪音
          ...(waitSec > 0 ? [{ label: '等待接通', value: fmtDuration(waitSec) }] : []),
          { label: '通话时长', value: talkSec > 0 ? fmtDuration(talkSec) : <EmptyValue /> },
          { label: '满意度', value: satisfaction != null ? <ScoreTag score={satisfaction} variant="satisfaction" /> : <EmptyValue /> },
          {
            label: '关联工单',
            value: detail.ticket_id ? (
              <Space size={6}>
                <Typography.Text
                  style={{ cursor: 'pointer', color: 'rgb(var(--link-6))' }}
                  onClick={() => onOpenTicket(detail.ticket_id as number)}
                >
                  #{detail.ticket_id}
                </Typography.Text>
                <StatusTag value={detail.ticket_status} />
              </Space>
            ) : (
              <EmptyValue />
            ),
          },
          // 技术值放最后并整行展示：UUID 是排障才用的长串，占前几位会把「时长/结果/关联工单」
          // 这些一眼要看的结论挤到下面，且窄列里必然折行
          {
            label: 'UUID',
            span: 2,
            value: detail.uuid ? (
              <span className="bcc-mono" style={{ wordBreak: 'break-all' }}>
                <CopyText text={detail.uuid}>{detail.uuid}</CopyText>
              </span>
            ) : (
              <EmptyValue />
            ),
          },
        ]}
      />
      {recordUrl && (
        <DetailSection title="通话录音">
          <Typography.Text type="secondary" style={{ fontSize: 12, display: 'block', marginBottom: 8 }}>
            录音时长约 {fmtDuration(talkSec || detail.duration)}（录音从接通时开始，与上方「通话时长」同轴，不含等待接通；时长以播放器进度为准，可拖动）
          </Typography.Text>
          <audio ref={audioRef} controls src={authUrl(recordUrl)} style={{ width: '100%' }} />
        </DetailSection>
      )}
      <DetailSection title="转写文本">
        <TranscriptView transcript={detail.transcript} caller={detail.caller} callee={detail.callee} direction={detail.direction} />
      </DetailSection>
      <DetailSection title="服务小结">
        {summary ? (
          <Descriptions
            column={2}
            size="small"
            data={[
              { label: '分类', value: summary.category || <EmptyValue /> },
              { label: '处理结果', value: summary.result || <EmptyValue /> },
              {
                label: '是否回访',
                value: summary.follow_up ? <PillTag color="orange">需回访</PillTag> : '否',
              },
              { label: '坐席', value: summary.agent_name || <EmptyValue /> },
              { label: '备注', value: summary.note || <EmptyValue />, span: 2 },
              { label: '填写时间', value: fmtTime(summary.updated_at || summary.created_at), span: 2 },
            ]}
          />
        ) : (
          <Space>
            <Typography.Text type="secondary">未填写</Typography.Text>
            <Button size="small" type="outline" onClick={onSupplementSummary}>
              补录
            </Button>
          </Space>
        )}
      </DetailSection>
    </Space>
  );
}
